/**
 * /university/tuners/$tunerId — tuner profile + activity feed.
 */
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ChevronLeft, Loader2 } from 'lucide-react';
import {
  effectivenessTone,
  fmtCount,
  fmtRelative,
  fmtScore,
  TONE_TEXT_CLASS,
} from '../../../../components/university/formatters';
import { learningApi, learningKeys } from '../../../../lib/api/learning';
import { cn } from '../../../../lib/utils';

function TunerDetailRoute() {
  const { tunerId } = Route.useParams();

  const tunerQ = useQuery({
    queryKey: learningKeys.tuner(tunerId),
    queryFn: () => learningApi.getTuner(tunerId),
  });
  const editsQ = useQuery({
    queryKey: learningKeys.tunerEdits(tunerId, 50),
    queryFn: () => learningApi.listTunerEdits(tunerId, 50),
  });

  if (tunerQ.isPending) {
    return <div className="p-6 text-[12px] text-muted-foreground">Loading tuner…</div>;
  }
  if (tunerQ.isError || !tunerQ.data) {
    return (
      <div className="p-6">
        <Link
          to="/university/tuners"
          className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3 w-3" aria-hidden /> Tuners
        </Link>
        <div className="mt-3 rounded border border-destructive/50 bg-destructive/5 p-3 text-[12px] text-destructive">
          {(tunerQ.error as Error)?.message ?? 'Tuner not found.'}
        </div>
      </div>
    );
  }

  const tuner = tunerQ.data;
  const edits = editsQ.data ?? [];
  const isActive = tuner.activeSince !== null;

  return (
    <section className="mx-auto flex w-full max-w-3xl flex-col gap-5 p-6">
      <header className="flex flex-col gap-2">
        <Link
          to="/university/tuners"
          className="inline-flex w-fit items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3 w-3" aria-hidden /> Tuners
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span
                aria-hidden
                className={cn(
                  'h-2 w-2 shrink-0 rounded-full',
                  isActive ? 'bg-success' : 'bg-muted-foreground/40',
                )}
              />
              <h1 className="text-[16px] font-semibold tracking-tight text-foreground">
                {tuner.displayName}
              </h1>
            </div>
            <span className="font-mono text-[12px] text-muted-foreground">
              user_id {tuner.userId.slice(0, 8)}…
            </span>
            <span className="text-[12px] text-muted-foreground">
              {tuner.scopeDepartmentKeys.length > 0
                ? `Scope: ${tuner.scopeDepartmentKeys.join(', ')}`
                : 'All departments'}
              {tuner.activeSince ? ` · active since ${fmtRelative(tuner.activeSince)}` : ''}
            </span>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Edits" value={tuner.editsTotal} />
        <StatCard label="Approvals" value={tuner.approvalsTotal} />
        <StatCard label="Rejected" value={tuner.rejectedTotal} />
      </div>

      <section className="flex flex-col gap-2">
        <header className="flex items-center justify-between">
          <h2 className="text-[13px] font-semibold text-foreground">Recent activity</h2>
          {editsQ.isFetching ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> syncing…
            </span>
          ) : null}
        </header>
        {edits.length === 0 ? (
          <div className="px-2 py-3 text-[12px] text-muted-foreground/70">
            No recent training-session activity.
          </div>
        ) : (
          <ol className="flex flex-col gap-2">
            {edits.map((edit) => (
              <li
                key={edit.id}
                className="flex items-center justify-between rounded border border-border bg-background px-3 py-2"
              >
                <div className="flex min-w-0 flex-col">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-[12px] font-semibold text-foreground">
                      {edit.skillName || 'Unknown skill'}
                    </span>
                    {edit.skillKey ? (
                      <span className="truncate font-mono text-[11px] text-muted-foreground">
                        {edit.skillKey}
                      </span>
                    ) : null}
                  </div>
                  <span className="truncate text-[11px] text-muted-foreground">
                    {edit.diffSummary} · {fmtRelative(edit.createdAt)}
                  </span>
                </div>
                <div className="flex flex-col items-end">
                  <span
                    className={cn(
                      'font-mono text-[12px] tabular-nums',
                      TONE_TEXT_CLASS[effectivenessTone(edit.effectivenessAfter)],
                    )}
                  >
                    {fmtScore(edit.effectivenessAfter)}
                  </span>
                  {edit.effectivenessDelta != null ? (
                    <span
                      className={cn(
                        'text-[11px]',
                        edit.effectivenessDelta >= 0 ? 'text-success' : 'text-destructive',
                      )}
                    >
                      {edit.effectivenessDelta >= 0 ? '+' : ''}
                      {(edit.effectivenessDelta * 100).toFixed(1)}%
                    </span>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
    </section>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col gap-1 rounded border border-border bg-background p-3">
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="font-mono text-2xl font-semibold tabular-nums text-foreground">
        {fmtCount(value)}
      </span>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/university/tuners/$tunerId')({
  component: TunerDetailRoute,
});
